import { EyeOutlined, HeartOutlined, HeartTwoTone } from '@ant-design/icons';
import { getYoutubeEmbedSrc } from '@helpers/youtube';
import useMedia from '@hooks/Media/useMedia';
import {
  Button,
  Col,
  Divider,
  Modal,
  Row,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { IMedia, MediaType } from 'interface/medias';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import './style.less';

export type MediaModalProps = {
  media: IMedia;
  open: boolean;
  onClose: () => void;
};

const MediaModal: React.FC<MediaModalProps> = ({ media, open, onClose }) => {
  const mediaId = useMemo(
    () => (typeof media === 'string' ? media : media.id),
    [media],
  );
  const { data, loading, like, unlike, view } = useMedia(mediaId);

  const onClickLike = useMemo(
    () => _.throttle(data?.liked ? unlike : like, 5000),
    [data?.liked, like, unlike],
  );

  const onPlayMedia = useCallback(() => {
    view();
  }, [view]);

  return (
    <Modal
      open={open}
      destroyOnClose
      onCancel={onClose}
      footer={null}
      width="80%"
      className="media-modal"
      title={<Typography.Title />}
      centered
    >
      <Space direction="vertical" className="full-width">
        {media.mediaType === MediaType.YOUTUBE ? (
          <div className="video_wrapper">
            <iframe
              className="youtube-player"
              src={getYoutubeEmbedSrc(media.source)}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            />
          </div>
        ) : (
          <video onPlay={onPlayMedia} controls autoPlay>
            <source src={media.source} type="video/mp4" />
          </video>
        )}
        <Space>
          <Typography.Text strong className="media-title">
            {media.name}
          </Typography.Text>
          {media.tags.map((tag) => (
            <Tooltip key={tag.id} title={tag.description}>
              <Tag color="magenta">{tag.label}</Tag>
            </Tooltip>
          ))}
        </Space>
        <Typography.Paragraph type="secondary">
          {media.description}
        </Typography.Paragraph>
        <Divider />
        <Row
          className="media-control-panel"
          justify="center"
          align="middle"
          gutter={[20, 20]}
        >
          <Col>
            <Button onClick={onClickLike} type="text" className="like-holder">
              <Space>
                <Typography.Text>
                  {loading ? <Skeleton.Button active /> : data?.likes ?? '-'}
                </Typography.Text>
                {data?.liked ? <HeartTwoTone /> : <HeartOutlined />}
              </Space>
            </Button>
          </Col>
          <Col>
            <Button type="text" className="like-holder">
              <Space className="like-holder">
                <Typography.Text>
                  {loading ? <Skeleton.Button active /> : data?.views ?? '-'}
                </Typography.Text>
                <EyeOutlined />
              </Space>
            </Button>
          </Col>
        </Row>
      </Space>
    </Modal>
  );
};

export default MediaModal;
